import { format, parseISO } from "date-fns";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { EmptyState } from "../../ui";
import ChartCard from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import {
  axisProps,
  chart,
  sentimentLabels,
  sentimentRating,
  sentimentScale,
} from "./theme";

const shortDate = (d) => format(parseISO(d), "d MMM");

function SentimentTrendChart({ daily, average, style }) {
  const data = daily.filter((d) => d.avg_sentiment !== null);
  if (!data.length) {
    return (
      <ChartCard
        title="Sentiment trend"
        subtitle="Average sentiment of summarised calls per day (0-100)"
        height={240}
        style={style}
      >
        <EmptyState
          style={{
            height: "100%",
            boxSizing: "border-box",
            justifyContent: "center",
          }}
        >
          There aren't enough summarised calls in this period to show a trend.
        </EmptyState>
      </ChartCard>
    );
  }

  const values = data.map((d) => d.avg_sentiment);
  // Zoomed to the data (in steps of 10) so day-to-day movement is visible.
  const domain = [
    Math.max(0, Math.floor((Math.min(...values) - 5) / 10) * 10),
    Math.min(100, Math.ceil((Math.max(...values) + 5) / 10) * 10),
  ];
  const ticks = Array.from(
    { length: (domain[1] - domain[0]) / 10 + 1 },
    (_, i) => domain[0] + i * 10,
  );

  return (
    <ChartCard
      title="Sentiment trend"
      subtitle="Average sentiment of summarised calls per day (0-100)"
      height={240}
      style={style}
      table={{
        columns: ["Date", "Avg sentiment"],
        rows: data.map((d) => [shortDate(d.date), Math.round(d.avg_sentiment)]),
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 8, right: 56, bottom: 0, left: -12 }}
        >
          {/* Faint bands for the negative (<40) and positive (>60) zones. */}
          <ReferenceArea
            y1={0}
            y2={40}
            fill={sentimentScale[1]}
            fillOpacity={0.06}
            ifOverflow="hidden"
          />
          <ReferenceArea
            y1={60}
            y2={100}
            fill={sentimentScale[5]}
            fillOpacity={0.06}
            ifOverflow="hidden"
          />
          <CartesianGrid vertical={false} stroke={chart.grid} />
          <XAxis
            dataKey="date"
            tickFormatter={shortDate}
            minTickGap={24}
            {...axisProps}
          />
          <YAxis
            domain={domain}
            ticks={ticks}
            {...axisProps}
            axisLine={false}
          />
          <ReferenceLine
            y={average}
            stroke="rgba(255,255,255,0.35)"
            label={{
              value: `Avg ${Math.round(average)}`,
              position: "right",
              fill: chart.tick,
              fontSize: 12,
            }}
          />
          <Tooltip
            cursor={{ stroke: chart.axis }}
            content={
              <ChartTooltip
                title={(label) => format(parseISO(label), "EEE d MMM")}
                rows={(payload) => {
                  const score = Math.round(payload[0].value);
                  const rating = sentimentRating(score);
                  return [
                    {
                      label: "Score",
                      value: score,
                      color: sentimentScale[rating],
                    },
                    { label: "Rating", value: sentimentLabels[rating] },
                  ];
                }}
              />
            }
          />
          <Line
            type="monotone"
            dataKey="avg_sentiment"
            stroke="#2cc2bc"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, stroke: chart.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export default SentimentTrendChart;
